import { DepositAddressResponse, RouteResponse } from "@0xsquid/squid-types";
import { useMutation } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { CHAIN_IDS } from "../../core";
import {
  isChainflipDepositRoute,
  isDepositAddressDirectTransferRoute,
} from "../../services/internal/transactionService";
import {
  PaymentMethod,
  useDepositAddressStore,
} from "../store/useDepositAddressStore";
import { useSquidStore } from "../store/useSquidStore";
import { useSwap } from "./useSwap";

export function useDepositAddress(squidRoute?: RouteResponse["route"]) {
  const { selectedPaymentMethod, depositAddress } = useDepositAddressStore(
    (state) => ({
      selectedPaymentMethod: state.selectedPaymentMethod,
      depositAddress: state.deposit?.depositAddress,
    })
  );
  const { setDeposit, setPaymentMethod, deposit } = useDepositAddressStore();
  const { squid } = useSquidStore();

  const { fromChain } = useSwap();

  const isAvailableAsPaymentMethod = useMemo(() => {
    if (!fromChain?.chainId) return false;

    const chainsSupportingDepositAddress = [
      CHAIN_IDS.BITCOIN,
      CHAIN_IDS.SOLANA,
      CHAIN_IDS.CANTON,
    ] as string[];

    return chainsSupportingDepositAddress.includes(fromChain.chainId);
  }, [fromChain?.chainId]);

  const routeProvidesDirectDepositAddress =
    !!squidRoute?.transactionRequest &&
    isDepositAddressDirectTransferRoute(squidRoute.transactionRequest);

  const swapWillGenerateDepositAddress =
    isChainflipDepositRoute(squidRoute) || routeProvidesDirectDepositAddress;

  const paymentMethod: PaymentMethod = isAvailableAsPaymentMethod
    ? selectedPaymentMethod
    : "connectedWallet";

  const routeSupportsDepositAddress =
    !squidRoute?.transactionRequest || swapWillGenerateDepositAddress;

  const isDepositAddressActive =
    paymentMethod === "depositAddress" && routeSupportsDepositAddress;

  const closeDepositChannel = useCallback(() => {
    setDeposit(null);
  }, [setDeposit]);

  const getRouteWithDeposit = useMutation(
    async ({ route }: { route: RouteResponse["route"] }): Promise<void> => {
      if (!squid || !route.transactionRequest) {
        throw new Error("Missing required params");
      }

      if (isChainflipDepositRoute(route)) {
        const depositAddressResponse = (await squid.executeRoute({
          signer: {} as any,
          route,
        })) as DepositAddressResponse;

        setDeposit({
          amount: depositAddressResponse.amount,
          depositAddress: depositAddressResponse.depositAddress,
          statusTrackingId: depositAddressResponse.chainflipStatusTrackingId,
        });

        return;
      } else if (
        isDepositAddressDirectTransferRoute(route.transactionRequest)
      ) {
        // Canton case
        const orderHash = route.transactionRequest.data;

        setDeposit({
          amount: route.params.fromAmount ?? "",
          depositAddress: route.transactionRequest.target,
          statusTrackingId: orderHash,
          memo: orderHash,
        });
        return;
      }
    }
  );

  return {
    paymentMethod,
    isDepositAddressActive,
    isAvailableAsPaymentMethod,
    swapWillGenerateDepositAddress,
    setPaymentMethod,
    getRouteWithDeposit,
    depositAddress,
    closeDepositChannel,
    depositData: deposit,
  };
}
